import { useContext, useEffect, useRef } from 'react';
import {
  ErrorNoticeContext,
  type ErrorNoticeContextValue,
} from '../components/ErrorNoticeProvider';

// The seam every error notification goes through (ADR 0021). Writes use it directly, from the
// handler that learned the write failed.
export function useErrorNotice(): ErrorNoticeContextValue {
  const context = useContext(ErrorNoticeContext);
  if (context === null) {
    // Loud, like the auth seam: a caller mounted outside the shell would otherwise report its
    // failures into the void, and nothing on screen would say the report was lost.
    throw new Error('useErrorNotice must be used within <ErrorNoticeProvider> (mounted by <SearchShell>).');
  }
  return context;
}

// Failed READS report from render, not from a handler: there is no event to hang off — the query's
// error is a state the section arrives in. Keyed on the source, so the retry that fails again
// refreshes one notice instead of stacking a second.
//
// It deliberately does NOT withdraw the notice when the read later succeeds: with
// `refetchOnWindowFocus` off, that only happens on a remount, which takes the caller — and its
// effect — with it. A notice outliving the section it is about is the accepted cost of the rule
// that a notice never leaves on its own.
export function useNoticeOnError(active: boolean, id: string, message: string): void {
  const { notify } = useErrorNotice();
  useEffect(() => {
    if (!active) return undefined;
    notify(id, message);
    return undefined;
  }, [active, id, message, notify]);
}

// Failed WRITES are the other half. A write failure is an event, not a state: the flow that raised
// it has to be told the failure has been reported, so its control goes back to idle and the action's
// trigger is usable again — while the notice stays up until the agent dismisses it. `message` is
// the copy the caller resolved from the failure (never a backend string), or null when there is
// nothing to report.
export function useReportFailure(
  id: string,
  message: string | null,
  onReported: () => void,
): void {
  const { notify } = useErrorNotice();
  const reported = useRef(onReported);
  // Held in a ref and kept OUT of the reporting effect's deps: every caller passes an inline
  // callback, so a dep on it would re-run the report on every render — and re-raise the notice the
  // agent had just dismissed.
  useEffect(() => {
    reported.current = onReported;
  }, [onReported]);
  useEffect(() => {
    if (message === null) return undefined;
    notify(id, message);
    reported.current();
    return undefined;
  }, [id, message, notify]);
}
